import { PERIODO_ULTIMOS_30 } from '../../commons/filters/periodos.js';

/**
 * VEC-3315 — declaración de los filtros del historial global de movimientos para <GridFilterPanel />.
 *
 * Módulo aparte porque lo consumen DOS lados: `InventarioMovimientosGridFiltros` (para renderizar) e
 * `InventarioMovimientosGrid` (para calcular el query por defecto —y para saber cuándo el query
 * vigente DIFIERE de ese default, que es lo que decide si se ve la franja de resultados).
 *
 * UNA CLAVE DEL QUERY NO ES UN CAMPO DEL PANEL: `ubicacionType`, que agrega la grilla cuando hay
 * depósitos elegidos. Mismo motivo que en la grilla de stock: la columna de la base es polimórfica,
 * el par (tipo, ids) viaja completo o no viaja, y el tipo no es una decisión del usuario.
 */

export const INVENTARIO_MOVIMIENTOS_FILTROS_STORAGE_KEY = 'InventarioMovimientosGrid_filtros';

/**
 * ESTA GRILLA SÍ TIENE UN FILTRO POR DEFECTO: los últimos 30 días.
 *
 * `item_stock_movimientos` es una tabla de auditoría que solo crece, así que abrir sin acotar sería
 * pedir el historial completo del cliente en la primera pantalla. El backend aplica la misma ventana
 * cuando no le mandan fechas (ItemStockService::DIAS_POR_DEFECTO), pero el default se declara igual
 * acá: si solo viviera en el backend, el control se vería vacío mientras el listado sale filtrado, y
 * el usuario no tendría cómo saber qué está viendo.
 *
 * El período se guarda como la OPCIÓN elegida, no como el rango ya calculado: los períodos relativos
 * se recalculan en cada serialización, así que unos "últimos 30 días" guardados hace una semana
 * siguen significando los últimos 30 días de hoy.
 */
export const getInventarioMovimientosFilterDefaults = (intl) => ({
	periodo: {
		id: PERIODO_ULTIMOS_30,
		label: intl.formatMessage({ id: 'Ultimos_30_dias', defaultMessage: 'Últimos 30 días' }),
	},
});

const ANCHO_MEDIO = { xs: 12, sm: 6, md: 3, lg: 3 };

export const buildInventarioMovimientosFilterFields = () => [
	{
		// El período NO viaja con su propio nombre: se serializa a las dos claves del rango. La
		// opción elegida es UI; el backend recibe fechas.
		name: 'periodo',
		type: 'periodo',
		startName: 'fechaDesde',
		endName: 'fechaHasta',
		messageId: 'inventarioMovimientos.grid.filtros_periodo',
		label: 'Período',
		size: ANCHO_MEDIO,
	},
	{
		// Múltiple: serializa "INGRESO,AJUSTE". Hoy el catálogo tiene UN tipo (INGRESO); los otros
		// cinco los agrega cada card de la épica con una constante en el modelo, sin migración, y
		// acá van a aparecer solos en cuanto se sumen a las opciones del panel.
		name: 'tipo',
		type: 'multiselect',
		messageId: 'inventarioMovimientos.grid.filtros_tipo',
		label: 'Tipo',
		size: ANCHO_MEDIO,
	},
	{
		// Múltiple, a diferencia del de la grilla de stock: acá cada fila es UN movimiento, así que
		// sumar depósitos une historiales en vez de sumar existencias. El endpoint lo acepta como
		// lista (ItemStockValidation::listaDeIds).
		name: 'ubicacionId',
		type: 'multiselect',
		messageId: 'inventarioMovimientos.grid.filtros_deposito',
		label: 'Depósito',
		size: ANCHO_MEDIO,
	},
	{
		// Texto y no un desplegable de ítems: `/items/simple-search` hace un `get()` SIN limit y con
		// miles de ítems escanea la tabla entera, así que no se lo puede usar para alimentar un
		// filtro. El backend resuelve este texto con un LIKE sobre número de parte y descripción.
		// Cuando ese endpoint se arregle, esto pasa a ser un `pastableMultiselect`.
		name: 'itemBusqueda',
		type: 'text',
		messageId: 'inventarioMovimientos.grid.filtros_item',
		label: 'Ítem',
		size: ANCHO_MEDIO,
	},
];
